import React, { useContext, useReducer } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import DeviationFlag from '@wix/da-ds/pkg/Indicators/DeviationFlag';
import DeviationFlagCard from './_partials/DeviationFlagCard';
import PositionOnlyPopperPopover from '@wix/da-ds/pkg/popper/presets/PositionOnlyPopperPopover';
import { isHyped, isPublished } from '../../../DeviationViews/_helpers';
import s from './DeviationFlags.scss';

export interface Props {
  deviation: PapiDeviation;
  disableLabel?: boolean;
  alwaysExpanded?: boolean;
  className?: string;
}

const createFlagStateReducer =
  (isMobile: boolean) =>
  (
    state: {
      isDdListVisible: boolean;
      isDdHovering: boolean;
      isHypeListVisible: boolean;
      isHypeHovering: boolean;
      isStashListVisible: boolean;
      isStashHovering: boolean;
    },
    action: {
      variant: 'daily-deviation' | 'hype' | 'stash';
      gesture:
        | 'mouseEnter'
        | 'mouseLeave'
        | 'click'
        | 'transitionEnd'
        | 'close';
    }
  ) => {
    const { variant, gesture } = action;
    let visibleFlag:
      | 'isDdListVisible'
      | 'isHypeListVisible'
      | 'isStashListVisible';
    let hoverFlag: 'isDdHovering' | 'isHypeHovering' | 'isStashHovering';
    switch (variant) {
      case 'daily-deviation':
        visibleFlag = 'isDdListVisible';
        hoverFlag = 'isDdHovering';
        break;
      case 'hype':
        visibleFlag = 'isHypeListVisible';
        hoverFlag = 'isHypeHovering';
        break;
      case 'stash':
        visibleFlag = 'isStashListVisible';
        hoverFlag = 'isStashHovering';
        break;
      default:
        return state;
    }

    switch (gesture) {
      case 'mouseEnter':
        return isMobile
          ? state
          : {
              [visibleFlag]: true,
              [hoverFlag]: true,
            };
      case 'mouseLeave':
        return isMobile
          ? state
          : {
              ...state,
              [hoverFlag]: false,
            };
      case 'click':
        return isMobile
          ? state[visibleFlag]
            ? {
                ...state,
                [hoverFlag]: false,
              }
            : {
                [visibleFlag]: true,
                [hoverFlag]: true,
              }
          : state;
      case 'transitionEnd':
        return state[hoverFlag]
          ? state
          : {
              ...state,
              [visibleFlag]: false,
            };
      case 'close':
        return {
          ...state,
          [hoverFlag]: false,
        };
      default:
        return state;
    }
  };

const DeviationFlags: React.FC<Props> = ({
  deviation,
  deviation: { dailyDeviation },
  alwaysExpanded,
  disableLabel,
  className,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const [
    {
      isDdListVisible,
      isDdHovering,
      isHypeListVisible,
      isHypeHovering,
      isStashListVisible,
      isStashHovering,
    },
    dispatch,
  ] = useReducer(createFlagStateReducer(isMobile), {
    isDdListVisible: false,
    isDdHovering: false,
    isHypeListVisible: false,
    isHypeHovering: false,
    isStashListVisible: false,
    isStashHovering: false,
  });

  const hyped = isHyped(deviation);
  const isStash = !isPublished(deviation);

  if (!dailyDeviation && !hyped && !isStash) {
    return null;
  }

  const bothFlags = dailyDeviation && hyped;

  return (
    <div
      className={classnames(
        s['root'],
        s['root-updated'],
        bothFlags && s['both-flags']
      )}
    >
      {dailyDeviation && (
        <div
          className={classnames(
            s['flag-root'],
            alwaysExpanded && s['always-expanded'],
            isDdHovering && s['hovering'],
            disableLabel && s['disable-label'],
            className
          )}
          onClick={() =>
            dispatch({
              variant: 'daily-deviation',
              gesture: 'click',
            })
          }
          onMouseEnter={() =>
            dispatch({
              variant: 'daily-deviation',
              gesture: 'mouseEnter',
            })
          }
          onMouseLeave={() =>
            dispatch({
              variant: 'daily-deviation',
              gesture: 'mouseLeave',
            })
          }
          onTransitionEnd={(e: React.TransitionEvent<HTMLDivElement>) => {
            if (e.propertyName === 'opacity') {
              dispatch({
                variant: 'daily-deviation',
                gesture: 'transitionEnd',
              });
            }
          }}
        >
          <PositionOnlyPopperPopover
            placement="top"
            secondaryPlacement={isMobile ? 'end' : 'start'}
            wrapperClassName={s['tooltip']}
            usePortal={false}
            eventsEnabled={isDdHovering}
            onClickOutside={() =>
              dispatch({
                variant: 'daily-deviation',
                gesture: 'close',
              })
            }
            popoverContent={({ placement }) => (
              <div
                className={classnames(
                  s['flag-list'],
                  s[placement],
                  isDdListVisible && s['flag-list-visible']
                )}
              >
                <DeviationFlagCard
                  variant="daily-deviation"
                  deviation={deviation}
                />
              </div>
            )}
          >
            <div className={s['base']}>
              <DeviationFlag
                variant="daily-deviation"
                size={isMobile ? 'medium' : 'large'}
                className={s['flag']}
              />
              <div className={s['collapsed-title']}>
                {t('actionbar.flags.dailyDeviation')}
              </div>
            </div>
          </PositionOnlyPopperPopover>
        </div>
      )}
      {hyped && (
        <div
          className={classnames(
            s['flag-root'],
            alwaysExpanded && s['always-expanded'],
            isHypeHovering && s['hovering'],
            disableLabel && s['disable-label'],
            className
          )}
          onClick={() =>
            dispatch({
              variant: 'hype',
              gesture: 'click',
            })
          }
          onMouseEnter={() =>
            dispatch({
              variant: 'hype',
              gesture: 'mouseEnter',
            })
          }
          onMouseLeave={() =>
            dispatch({
              variant: 'hype',
              gesture: 'mouseLeave',
            })
          }
          onTransitionEnd={(e: React.TransitionEvent<HTMLDivElement>) => {
            if (e.propertyName === 'opacity') {
              dispatch({
                variant: 'hype',
                gesture: 'transitionEnd',
              });
            }
          }}
        >
          <PositionOnlyPopperPopover
            placement="top"
            secondaryPlacement={isMobile ? 'end' : 'start'}
            wrapperClassName={s['tooltip']}
            usePortal={false}
            eventsEnabled={isHypeHovering}
            onClickOutside={() =>
              dispatch({
                variant: 'hype',
                gesture: 'close',
              })
            }
            popoverContent={({ placement }) => (
              <div
                className={classnames(
                  s['flag-list'],
                  s[placement],
                  isHypeListVisible && s['flag-list-visible']
                )}
              >
                <DeviationFlagCard variant="hype" deviation={deviation} />
              </div>
            )}
          >
            <div className={s['base']}>
              <DeviationFlag
                variant="hype"
                size={isMobile ? 'medium' : 'large'}
                className={s['flag']}
              />
              <div className={s['collapsed-title']}>
                {t('actionbar.flags.hype')}
              </div>
            </div>
          </PositionOnlyPopperPopover>
        </div>
      )}

      {isStash && (
        <div
          className={classnames(
            s['flag-root'],
            alwaysExpanded && s['always-expanded'],
            isStashHovering && s['hovering'],
            disableLabel && s['disable-label'],
            className
          )}
          onClick={() =>
            dispatch({
              variant: 'stash',
              gesture: 'click',
            })
          }
          onMouseEnter={() =>
            dispatch({
              variant: 'stash',
              gesture: 'mouseEnter',
            })
          }
          onMouseLeave={() =>
            dispatch({
              variant: 'stash',
              gesture: 'mouseLeave',
            })
          }
          onTransitionEnd={(e: React.TransitionEvent<HTMLDivElement>) => {
            if (e.propertyName === 'opacity') {
              dispatch({
                variant: 'stash',
                gesture: 'transitionEnd',
              });
            }
          }}
        >
          <PositionOnlyPopperPopover
            placement="top"
            secondaryPlacement={isMobile ? 'end' : 'start'}
            wrapperClassName={s['tooltip']}
            usePortal={false}
            eventsEnabled={isStashHovering}
            onClickOutside={() =>
              dispatch({
                variant: 'hype',
                gesture: 'close',
              })
            }
            popoverContent={({ placement }) => (
              <div
                className={classnames(
                  s['flag-list'],
                  s[placement],
                  isStashListVisible && s['flag-list-visible']
                )}
              >
                <DeviationFlagCard variant="stash" deviation={deviation} />
              </div>
            )}
          >
            <div className={s['base']}>
              <DeviationFlag
                variant="stash"
                size={isMobile ? 'medium' : 'large'}
                className={s['flag']}
              />
              <div className={s['collapsed-title']}>
                {t('actionbar.flags.stash.title')}
              </div>
            </div>
          </PositionOnlyPopperPopover>
        </div>
      )}
    </div>
  );
};
DeviationFlags.displayName = 'DeviationFlags';

export default DeviationFlags;
